/**
 * Customers — a search-first list. There is deliberately NO "all customers" dump on load (2k+ accounts
 * is noise + a needless query); the operator searches by name / account number / owner, and the matching
 * accounts render as a paginated table. A row opens that account's Customer-360. account_type is a DECOY
 * and is never shown as a plan here.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Building2, Search } from "lucide-react";
import { EmptyState, ErrorState, Input, SkeletonRows, Table, type Column } from "@/components/ui";
import { PermissionGate } from "@/components/portal/PermissionGate";
import { MIN_SEARCH_CHARS, useSearch } from "@/features/search/useSearch";
import type { SearchResult } from "@/api/types";

type AccountHit = SearchResult["accounts"][number];

export function CustomersPage() {
  const { t } = useTranslation();
  return (
    <PermissionGate
      permission="customer:read"
      fallback={<ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />}
    >
      <CustomersBody />
    </PermissionGate>
  );
}

function CustomersBody() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const search = useSearch(term);
  const accounts = search.data?.accounts ?? [];

  const columns: Column<AccountHit>[] = [
    {
      key: "name",
      header: t("search.groups.accounts"),
      cell: (a) => (
        <span className="font-medium text-(--text)">
          {a.account_name ?? a.account_number ?? a.account_id}
        </span>
      ),
      sortValue: (a) => a.account_name,
    },
    {
      key: "number",
      header: t("customer.accountNumber"),
      cell: (a) => a.account_number ?? "—",
      sortValue: (a) => a.account_number,
      numeric: true,
    },
    {
      key: "owner",
      header: t("customer.owner"),
      cell: (a) => a.owner_email ?? "—",
      sortValue: (a) => a.owner_email,
    },
  ];

  return (
    <section aria-labelledby="customers-title" className="flex flex-col gap-5">
      <h1
        id="customers-title"
        className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
      >
        {t("nav.customers")}
      </h1>

      <div className="relative max-w-xl">
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-(--text-subtle)"
          aria-hidden
        />
        <Input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t("customers.searchPlaceholder")}
          aria-label={t("customers.searchPlaceholder")}
          autoComplete="off"
          spellCheck={false}
          autoFocus
          className="h-10 pl-10"
        />
      </div>

      {!search.canSearch ? (
        <EmptyState title={t("state.forbiddenTitle")} body={t("search.noPermission")} />
      ) : !search.active ? (
        <EmptyState
          icon={<Building2 className="size-8" />}
          title={t("customers.idleTitle")}
          body={t("customers.idleBody", { min: MIN_SEARCH_CHARS })}
        />
      ) : search.isLoading ? (
        <SkeletonRows rows={6} />
      ) : search.isError ? (
        <ErrorState error={search.error} onRetry={search.refetch} />
      ) : accounts.length === 0 ? (
        <EmptyState
          title={t("customers.empty")}
          body={t("search.emptyBody", { term: search.debounced })}
        />
      ) : (
        <Table
          columns={columns}
          rows={accounts}
          rowKey={(a) => a.account_id}
          caption={t("nav.customers")}
          onRowClick={(a) => navigate(`/customers/${encodeURIComponent(a.account_id)}`)}
        />
      )}
    </section>
  );
}
